import { setActiveSidebarTab } from 'client/actions/actionsManagement'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import { useWebsiteSettings } from 'client/store'
import { localeNames } from 'common/constants/localeTypes'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BackButton } from 'src/editor/shared/components/back-button'
import { ChevronDownIcon } from 'src/editor/shared/icons/chevron-down-icon'
import { DotsVerticalIcon } from 'src/editor/shared/icons/dots-vertical-icon'
import { PlusCircleIcon } from 'src/editor/shared/icons/plus-circle-icon'
import { SearchInputIcon } from 'src/editor/shared/icons/search-input-icon'
import { ActionMenu } from 'src/editor/shared/ui-lib/action-menu'
import { GhostIconButton, TextButton } from 'src/editor/shared/ui-lib/buttons'
import { Input } from 'src/editor/shared/ui-lib/input'
import { getLocalesOptions } from 'src/editor/shared/ui-lib/language-selector/utils/get-locales-options'
import { Switcher } from 'src/editor/shared/ui-lib/switcher'
import { setCurrentWebsiteLocale } from 'src/editor/store/website-settings/website-settings-actions'
import {
  getCurrentWebsiteLocale,
  getWebsiteLocales,
} from 'src/editor/store/website-settings/website-settings-selector'
import { WebsiteLanguageSwitcherIcon } from 'src/shared/entities/website-language-switcher/website-language-switcher-icon'
import { SidebarTabEnum } from 'src/shared/enums/website-enums'
import { LocoAssetsKeysType } from 'src/shared/i18next/types/loco-assets-keys-type'
import { CreateEditCategoryModal } from './components/create-edit-category-modal'
import {
  BlogActionItemEnum,
  BlogContentTabEnum,
  BlogPostFilterEnum,
} from './enums'
import { BlogCategoryFormModeEnum, BlogCategoryFormValues } from './types'

const blogContentTabs = [
  {
    value: BlogContentTabEnum.Posts,
    content: 'website_settings.sidebar.blog.content.posts',
  },
  {
    value: BlogContentTabEnum.Categories,
    content: 'website_settings.sidebar.blog.content.categories',
  },
]

const blogPostFilterLabels: Record<BlogPostFilterEnum, LocoAssetsKeysType> = {
  [BlogPostFilterEnum.AllPosts]:
    'website_settings.sidebar.blog.content.filter.all_posts',
  [BlogPostFilterEnum.ActivePosts]:
    'website_settings.sidebar.blog.content.filter.active_posts',
  [BlogPostFilterEnum.InactivePosts]:
    'website_settings.sidebar.blog.content.filter.inactive_posts',
}

export const WebsiteBlogSettings = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const currentWebsiteLocale = useWebsiteSettings(
    getCurrentWebsiteLocale,
  ) as PageLocaleEnum
  const websiteLocales = useWebsiteSettings(getWebsiteLocales)

  const [activeTab, setActiveTab] = useState<BlogContentTabEnum>(
    BlogContentTabEnum.Posts,
  )
  const [activePostFilter, setActivePostFilter] = useState<BlogPostFilterEnum>(
    BlogPostFilterEnum.AllPosts,
  )
  const [searchValue, setSearchValue] = useState('')

  const [isCategoryModalOpened, setCategoryModalOpened] = useState(false)
  const [categoryFormMode, setCategoryFormMode] =
    useState<BlogCategoryFormModeEnum>(BlogCategoryFormModeEnum.Create)

  const closeCategoryModal = () => {
    setCategoryModalOpened(false)
  }

  const openCreateCategoryModal = () => {
    setCategoryFormMode(BlogCategoryFormModeEnum.Create)
    setCategoryModalOpened(true)
  }

  const handleCreateBlogContent = () => {
    if (activeTab === BlogContentTabEnum.Categories) {
      openCreateCategoryModal()
    }
  }

  const handleBlogActionSelect = (action: BlogActionItemEnum) => {
    if (action === BlogActionItemEnum.Edit) {
      // TODO: Open edit blog modal.
    }
  }

  const handleSaveCategory = (values: BlogCategoryFormValues) => {
    // TODO: implement category saving API
  }

  const localeOptions = getLocalesOptions({
    t,
    localeList: websiteLocales,
  })

  const languageMenuItems = localeOptions.map(({ value, label }) => ({
    id: value,
    label,
    iconBefore: () => <WebsiteLanguageSwitcherIcon locale={value} />,
  }))

  return (
    <>
      <BackButton
        className="mb-4!"
        onClick={() =>
          dispatch(setActiveSidebarTab(SidebarTabEnum.PagesManagement))
        }
      />
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <span className="min-w-0 flex-1 truncate text-heading-4">
            {t('entity_settings.blog.title')}{' '}
            {/* TODO: set blog name if needed after discussion with PO */}
          </span>
          <div className="flex shrink-0 items-center gap-1">
            <ActionMenu
              items={languageMenuItems}
              onSelect={locale =>
                dispatch(setCurrentWebsiteLocale(locale as PageLocaleEnum))
              }
              triggerElement={setIsOpened => (
                <button
                  type="button"
                  onClick={() => setIsOpened(true)}
                  className="inline-flex h-9 items-center gap-1 rounded-md px-2 text-M text-textPrimary transition-all duration-150 hover:bg-bgSecondary"
                  title={t(localeNames[currentWebsiteLocale])}
                >
                  <WebsiteLanguageSwitcherIcon locale={currentWebsiteLocale} />
                  <ChevronDownIcon className="h-4 w-4" />
                </button>
              )}
            />
            <ActionMenu
              items={[
                {
                  id: BlogActionItemEnum.Edit,
                  label: t('website_settings.sidebar.blog.actions.edit'),
                },
              ]}
              onSelect={action =>
                handleBlogActionSelect(action as BlogActionItemEnum)
              }
              triggerElement={setIsOpened => (
                <GhostIconButton
                  onClick={() => setIsOpened(true)}
                  icon={<DotsVerticalIcon className="fill-iconSecondary" />}
                  title={t(
                    'website_settings.sidebar.pages_management.settings',
                  )}
                />
              )}
            />
          </div>
        </div>
        <hr className="border-border200" />
        {/* TODO: Add blog pages listing here when backend data is available. */}
        <div className="flex items-center justify-between">
          <span className="text-heading-4">
            {t('website_settings.sidebar.blog.content.title')}
          </span>
          <GhostIconButton
            onClick={handleCreateBlogContent}
            icon={<PlusCircleIcon />}
          />
        </div>
        <Switcher
          btnList={blogContentTabs}
          value={activeTab}
          update={setActiveTab}
        />
        {activeTab === BlogContentTabEnum.Posts && (
          <ActionMenu
            items={[
              {
                id: BlogPostFilterEnum.AllPosts,
                label: t(
                  'website_settings.sidebar.blog.content.filter.all_posts',
                ),
              },
              {
                id: BlogPostFilterEnum.ActivePosts,
                label: t(
                  'website_settings.sidebar.blog.content.filter.active_posts',
                ),
              },
              {
                id: BlogPostFilterEnum.InactivePosts,
                label: t(
                  'website_settings.sidebar.blog.content.filter.inactive_posts',
                ),
              },
            ]}
            onSelect={filter =>
              setActivePostFilter(filter as BlogPostFilterEnum)
            }
            triggerElement={setIsOpened => (
              <TextButton
                className="h-auto p-0 text-btnPrimary"
                onClick={() => setIsOpened(true)}
              >
                <span className="inline-flex items-center gap-1">
                  {t(blogPostFilterLabels[activePostFilter])}
                  <ChevronDownIcon className="h-4 w-4" />
                </span>
              </TextButton>
            )}
          />
        )}
        <Input
          type="search"
          value={searchValue}
          onChange={setSearchValue}
          placeholder={t('global.search')}
          endElement={<SearchInputIcon />}
        />
      </div>
      <CreateEditCategoryModal
        isOpened={isCategoryModalOpened}
        mode={categoryFormMode}
        onClose={closeCategoryModal}
        onSubmit={handleSaveCategory}
      />
    </>
  )
}
